import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

export const PLACEMENTS = [
  { value: "home_top", label: "Home — topo (logo abaixo do hero)" },
  { value: "home_middle", label: "Home — meio (entre categorias e destaques)" },
  { value: "home_bottom", label: "Home — final (antes de 'Como funciona')" },
  { value: "header_top", label: "Barra global no topo (todas as páginas)" },
  { value: "explore_top", label: "Página Explorar — topo" },
];

function slugify(s) {
  return (s || "").toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 50);
}

export default function CreateBannerModal({ open, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [bannerId, setBannerId] = useState("");
  const [manualId, setManualId] = useState(false);
  const [placement, setPlacement] = useState("home_middle");
  const [description, setDescription] = useState("");
  const [buttonLabel, setButtonLabel] = useState("");
  const [buttonLink, setButtonLink] = useState("");
  const [bgColor, setBgColor] = useState("#2A5C43");
  const [textColor, setTextColor] = useState("#FFFFFF");
  const [saving, setSaving] = useState(false);

  const effectiveId = manualId ? bannerId : slugify(title);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error("Informe um título"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/moderation/banners", {
        banner_id: effectiveId,
        title: title.trim(),
        placement,
        description,
        button_label: buttonLabel,
        button_link: buttonLink,
        bg_color: bgColor,
        text_color: textColor,
      });
      toast.success("Banner criado — abra 'Editar' para ajustar cores e outros detalhes.");
      onCreated(data);
      setTitle(""); setBannerId(""); setManualId(false); setDescription(""); setButtonLabel(""); setButtonLink("");
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="create-banner-modal" className="max-w-xl bg-white rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Novo banner</DialogTitle>
          <DialogDescription className="text-brand-textMuted">
            Crie um banner personalizado e escolha onde ele aparece no site.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <label className="block text-sm font-bold text-brand-text mb-1">Título</label>
            <Input data-testid="new-banner-title" required value={title} onChange={(e) => setTitle(e.target.value)} placeholder="ex: Newsletter da comunidade" className="bg-brand-bg border-brand-border rounded-2xl h-11" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-bold text-brand-text">Identificador interno</label>
              <button type="button" className="text-xs text-brand-primary font-bold" onClick={() => setManualId((v) => !v)}>
                {manualId ? "Gerar automático" : "Editar manualmente"}
              </button>
            </div>
            <Input data-testid="new-banner-id" value={effectiveId} disabled={!manualId} onChange={(e) => setBannerId(e.target.value)} className="bg-brand-bg border-brand-border rounded-2xl h-11 font-mono" />
          </div>
          <div>
            <label className="block text-sm font-bold text-brand-text mb-1">Onde aparecer no site</label>
            <select data-testid="new-banner-placement" value={placement} onChange={(e) => setPlacement(e.target.value)} className="w-full bg-brand-bg border border-brand-border rounded-2xl h-11 px-3 font-bold">
              {PLACEMENTS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-bold text-brand-text mb-1">Descrição (opcional)</label>
            <Textarea data-testid="new-banner-description" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} className="bg-brand-bg border-brand-border rounded-2xl" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-bold text-brand-text mb-1">Texto do botão</label>
              <Input data-testid="new-banner-btn-label" value={buttonLabel} onChange={(e) => setButtonLabel(e.target.value)} className="bg-brand-bg border-brand-border rounded-2xl h-11" />
            </div>
            <div>
              <label className="block text-sm font-bold text-brand-text mb-1">Link do botão</label>
              <Input data-testid="new-banner-btn-link" value={buttonLink} onChange={(e) => setButtonLink(e.target.value)} placeholder="https:// ou /rota" className="bg-brand-bg border-brand-border rounded-2xl h-11" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-brand-textMuted mb-1">Cor de fundo</label>
              <div className="flex gap-2">
                <input type="color" value={bgColor} onChange={(e) => setBgColor(e.target.value)} className="w-10 h-10 rounded-lg border border-brand-border cursor-pointer" />
                <Input value={bgColor} onChange={(e) => setBgColor(e.target.value)} className="bg-brand-bg border-brand-border rounded-xl h-10 font-mono text-sm flex-1" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-brand-textMuted mb-1">Cor do texto</label>
              <div className="flex gap-2">
                <input type="color" value={textColor} onChange={(e) => setTextColor(e.target.value)} className="w-10 h-10 rounded-lg border border-brand-border cursor-pointer" />
                <Input value={textColor} onChange={(e) => setTextColor(e.target.value)} className="bg-brand-bg border-brand-border rounded-xl h-10 font-mono text-sm flex-1" />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-full font-bold">Cancelar</Button>
            <Button type="submit" data-testid="new-banner-submit" disabled={saving} className="bg-brand-primary hover:bg-brand-primaryHover text-white rounded-full font-bold">
              {saving ? "Criando..." : "Criar banner"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
